import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef, Input, OnDestroy } from '@angular/core';
import { AnimationService } from 'src/app/engine/animation.service';
import { Store, Select } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { filter, throttleTime, takeUntil } from 'rxjs/operators';
import { EngineService, EngineEventsEnum } from 'src/app/engine/engine.service';
import { TimerangeModel } from 'src/app/engine/models/animation/timerange.model';
import { EngineState } from '../../state/engine.state';
import { PlayAnimation } from '../../state/engine.actions';
import { ChapterModel } from 'src/app/story/models/chapter.model';


@Component({
  selector: 'app-engine-scrubber',
  templateUrl: './engine-scrubber.component.html',
  styleUrls: ['./engine-scrubber.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EngineScrubberComponent implements OnInit, OnDestroy {
  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Input()
  theme: 'light' | 'dark' = 'dark';

  @Input()
  showPlay = true;

  @Input()
  showScrubber = true;

  playing = false;
  wasPlaying = false;

  private ngUnsubscribe = new Subject();

  private _percent = 0;
  public get percent() {
    return this._percent;
  }
  public set percent(value) {
    this._percent = value;

    const isPlaying = this.store.selectSnapshot(EngineState.getAnimationPlaying);
    if (!isPlaying) {
      this.scrubPercent(this.percent);
    }
  }

  scrub$ = new BehaviorSubject<number>(0);

  interactive = false;
  timeline = new TimerangeModel();

  constructor(
    private changeDetection: ChangeDetectorRef,
    private animationService: AnimationService,
    private store: Store,
    private engineService: EngineService
  ) { }

  ngOnInit() {
    this.bindApplicationEvents();
  }

  ngOnDestroy() {
    this.changeDetection.detach();

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.unsubscribe();
  }

  bindApplicationEvents() {
    // Listen for ready state
    this.selectedChapter$.pipe(
      filter((chapter) => chapter != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe( (chapter) => {
      this.interactive = chapter.readyState.model;
      this.percent = 0;
      this.changeDetection.markForCheck();
    });

    // Update scrubber on chapter change
    this.selectedChapterId$
      .pipe(
        filter(chapterId => !!chapterId),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapterId) => {
        this.initScrubber(chapterId);
      });

    // Listen to timeline ticks
    this.engineService.getEvent(EngineEventsEnum.TimelinePlaying)
      .pipe(
        throttleTime(50),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(event => this.tick(event.time));

    this.animationPlaying$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(playing => this.playing = playing);
  }

  handleInputMouseDown() {
    this.wasPlaying = this.playing;
    this.store.dispatch(new PlayAnimation(false));
  }

  handleInputMouseUp() {
    this.store.dispatch(new PlayAnimation(this.wasPlaying));
  }

  handlePlayClick() {
    this.play();
  }

  play() {
    const playing = !this.store.selectSnapshot(EngineState.getAnimationPlaying);
    this.store.dispatch(new PlayAnimation(playing));
  }

  initScrubber(chapterId: string) {
    this.timeline = this.animationService.getChapterLocalTimerange(chapterId);
    this.percent = 0;
  }

  tick(time: number) {
    const timerange = this.animationService.getChapterLocalTimerange(this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId));
    const localTime = this.animationService.mapGlobalTimeToLocal(time);
    

    this.percent = (localTime / timerange.lastTime) * 100;
    this.changeDetection.markForCheck();
  }

  scrubPercent(percent) {
    const localTime = (percent / 100) * this.timeline.lastTime;

    let which: 'first' | 'second' = 'first';
    if (percent === 0) {
      which = 'second';
    }
    if (percent === 100) {
      which = 'first';
    }
    
    const activeChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
    const globalTime = this.animationService.mapLocalTimeToGlobal(activeChapterId, localTime);
    
    this.animationService.scrub(globalTime, which);
  }
}
